import React, {useRef, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {EventAPIData, EventFormData} from "types/Event";
import EventForm from "pages/Event/Form";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import {DateTime} from "luxon";
import UpdateEventMode from "../Update";
import {UPDATE_MODES, UpdateMode} from "../Update/UpdateEventMode";
import {stripKeyFromObject} from "../../../services/object";

function MutateEvent() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const [showEventUpdateModeModal, setShowUpdateEventModeModal] = useState<boolean>(false);
  const formikHelpersRef = useRef<FormikHelpers<EventFormData> | null>(null);
  const formikValuesRef = useRef<EventFormData | null>(null);

  const {data: event, isLoading} = useQuery(
    ['event', {id}],
    () => ngage.events.get(Number(id)),
    {enabled: !!id && auth.can('events:read')}
  )

  const store = useMutation((data: EventAPIData) => ngage.events.store(data))
  const update = useMutation((data: EventAPIData & {originalEventId?: number}) => {
    const {originalEventId, ...eventData} = data;
    return ngage.events.update(originalEventId ?? parseInt(id as string), eventData);
  });

  const isUpdatingEvent = !!id;

  // Handle form submission
  async function submitData(values: EventFormData, formik: FormikHelpers<EventFormData>, action?: UpdateMode) {
    // Strip out site_id
    const eventData = stripKeyFromObject(values, 'site_id') as EventAPIData;

    // Update or store the event
    try {
      isUpdatingEvent ? await handleUpdate(eventData, action) : await handleStore(eventData);
      await invalidate('events.between');

      // Query params
      const date = values.starts_at ? DateTime.fromISO(values.starts_at).toFormat('yyyy-MM-dd') : DateTime.now().toFormat('yyyy-MM-dd');
      const siteParam = values.site_id ? `&site=${values.site_id}` : '';
      const typeParam = values.type_id ? `&type=${values.type_id}` : '';

      navigate(`/events?date=${date}${siteParam}${typeParam}`, {replace: !id});
    } catch (error) {
      // Bail if there is an error
      return;
    } finally {
      formik.setSubmitting(false);
    }
  }

  // Handle store mutation
  async function handleStore(values: EventAPIData) {
    try {
      await store.mutateAsync(values);
      notify("success", `${termFor('Event')} Created`, `${values.name} has been created`);
    } catch (error) {
      notify("error", `${termFor('Event')} Not Created`, `${values.name} could not be stored`);
      throw error;
    }
  }

  // Handle update mutation
  async function handleUpdate(values: EventAPIData, action?: UpdateMode) {
    const originalEventId = event?.original_event_id;
    const isOriginalEvent = !originalEventId;
    const isUpdateAllAction = action === UPDATE_MODES.ALL;
    const isUpdateSingularAction = action === UPDATE_MODES.SINGULAR;

    let eventData = values as EventAPIData & {originalEventId?: number};

    // Override parts of the event data if we are updating all events in the series and the event is not the original event
    if (isUpdateAllAction && !isOriginalEvent) {
      try {
        const originalEvent = await ngage.events.get(originalEventId);
        if (!originalEvent) return;

        // Get the event start and end dates in UTC
        const eventStartDate = DateTime.fromISO(eventData.starts_at, {zone: 'utc'});
        const eventEndDate = DateTime.fromISO(eventData.ends_at, {zone: 'utc'});
        const originalEventStartDate = originalEvent.starts_at.setZone('utc');
        const originalEventEndDate = originalEvent.ends_at.setZone('utc');

        // Override the time of the original event with the time of the event being updated
        const combinedEventStartDate = originalEventStartDate.set({
          hour: eventStartDate.hour,
          minute: eventStartDate.minute,
          second: eventStartDate.second,
          millisecond: eventStartDate.millisecond,
        });

        const combinedEventEndDate = originalEventEndDate.set({
          hour: eventEndDate.hour,
          minute: eventEndDate.minute,
          second: eventEndDate.second,
          millisecond: eventEndDate.millisecond,
        });

        eventData = {
          ...eventData,
          starts_at: combinedEventStartDate.toISO(),
          ends_at: combinedEventEndDate.toISO(),
          originalEventId: originalEventId,
        }
      } catch(error) {
        formikHelpersRef.current?.setSubmitting(false);
        throw new Error('Original event not found');
      }
    }

    // Add an action if we are updating a singular event in the series
    if (isUpdateSingularAction) {
      eventData = {
        ...eventData,
        action: UPDATE_MODES.SINGULAR,
      }
    }

    try {
      await update.mutateAsync(eventData);
      notify("success", `${termFor('Event')} Updated`, `${eventData.name} has been updated`);
      await invalidate(['event']);
    } catch (error) {
      notify("error", `${termFor('Event')} Not Updated`, `${eventData.name} could not be updated`);
      throw error;
    }
  }

  // Handle checking what type of event update to carry out
  function handleSubmit(values: EventFormData, formik: FormikHelpers<EventFormData>) {
    const isEventSeries = values.recurrence;

    // Show event mode modal if we are updating an event in a series
    if (isUpdatingEvent && isEventSeries) {
      setShowUpdateEventModeModal(true);
      formikHelpersRef.current = formik;
      formikValuesRef.current = values;
      return;
    }

    // Else handle standard submit for single events
    submitData(values, formik).catch(console.error);
  }

  function handleUpdateSeries(updateMode: UpdateMode) {
    if (!formikHelpersRef.current || !formikValuesRef.current) return;

    submitData(formikValuesRef.current, formikHelpersRef.current, updateMode).catch(console.error);
    setShowUpdateEventModeModal(false);
  }

  function handleCancelUpdateModal() {
    formikHelpersRef.current?.setSubmitting(false);
    setShowUpdateEventModeModal(false);
  }

  // Prevent unauthorised viewing
  if (auth.cannot('events:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    [termFor('Events')]: '/',
    'Calendar': '/events',
    [`New ${termFor('Event')}`]: '/events/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    [termFor('Events')]: '/',
    'Calendar': '/events',
    [`Edit ${termFor('Event')}`]: `/events/${id}/edit`
  }

  return (
    <Page
      title={id ? `Edit ${termFor('Event')}` : `New ${termFor('Event')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <EventForm event={event} onSubmit={handleSubmit} />

      {/* Event mode confirmation modal */}
      <UpdateEventMode
        show={showEventUpdateModeModal}
        onCancel={handleCancelUpdateModal}
        onSelectUpdateMode={handleUpdateSeries}
      />
    </Page>
  );
}

export default MutateEvent;